import { FavoriteEndPoint } from '@/modules/favorite/enums/favorite-end-point.enum.ts';
import { FavoriteAdapter } from '@/modules/favorite/adapters/favorite.adapter.ts';
import { FavoriteFolderUtil } from '@/modules/favorite/utils/favorite-folder.util.ts';
import { FavoriteErrorUtil } from '@/modules/favorite/utils/favorite-error.util.ts';
import { TableId } from '@/modules/common/enums/table-id.enum.ts';
import {
  FAVORITE_EXTRA_TABLE_IDS,
  FAVORITE_FOLDER_ID_ATTEMPTS,
  FOLDER_TABLE_ID,
} from '@/modules/favorite/consts/favorite.consts.ts';
import Api from '@/modules/core/api/api.ts';
import GatewayApi from '@/modules/core/api/gateway.api.ts';
import { GatewayFilter } from '@/modules/core/utils/gateway-filter.ts';
import type { ItemsDto } from '@/modules/common/dto/items.dto.ts';
import type { FavoriteAddDto, FavoriteDetailDto, FavoriteDto } from '@/modules/favorite/dto/favorite.dto.ts';
import type { FavoriteAdd } from '@/modules/favorite/types/favorite.type.ts';

export class FavoriteApi {
  static async getShortList(tableIds: TableId[], signal: AbortSignal) {
    const response = await GatewayApi.execute<ItemsDto<FavoriteDetailDto>>(FavoriteEndPoint.GetList, {
      filter: GatewayFilter.and(GatewayFilter.in('PARENT_TABLE_ID', [...tableIds, FOLDER_TABLE_ID])),
      signal,
    });

    return FavoriteAdapter.mapCardList(response);
  }

  static async getCardList(tableId: TableId, signal: AbortSignal) {
    return FavoriteApi.getShortList([tableId, ...(FAVORITE_EXTRA_TABLE_IDS[tableId] ?? []), TableId.Comments], signal);
  }

  static async get(id: number, signal: AbortSignal) {
    const response = await GatewayApi.execute<ItemsDto<FavoriteDetailDto>>(FavoriteEndPoint.GetList, {
      filter: GatewayFilter.and(GatewayFilter.eq('FAV_ID', id)),
      signal,
    });

    return FavoriteAdapter.mapList(response)[0];
  }

  static async getFolders(signal?: AbortSignal) {
    const response = await GatewayApi.execute<ItemsDto<FavoriteDto>>(FavoriteEndPoint.GetShortList, {
      filter: GatewayFilter.and(GatewayFilter.eq('PARENT_TABLE_ID', FOLDER_TABLE_ID)),
      signal,
    });

    return FavoriteAdapter.mapShortList(response);
  }

  static async add({ tableId, parentId, name, parents }: FavoriteAdd, signal?: AbortSignal): Promise<number> {
    const response: FavoriteAddDto = await Api.post(
      FavoriteEndPoint.Add,
      { parent_table_id: tableId, parent_id: parentId, name, ...(parents && { parents }) },
      signal
    );

    return response.fav_id;
  }

  static async addFolder(name: string, parents?: string, signal?: AbortSignal, attempt = 1): Promise<number> {
    const folders = await FavoriteApi.getFolders(signal);

    try {
      return await FavoriteApi.add(
        { tableId: FOLDER_TABLE_ID, parentId: FavoriteFolderUtil.nextParentId(folders), name, parents },
        signal
      );
    } catch (error) {
      if (attempt >= FAVORITE_FOLDER_ID_ATTEMPTS || !FavoriteErrorUtil.isAlreadyExists(error)) {
        throw error;
      }

      return FavoriteApi.addFolder(name, parents, signal, attempt + 1);
    }
  }

  static async addToNewFolder(favorite: FavoriteAdd, folderName: string, signal?: AbortSignal) {
    const folderId = await FavoriteApi.addFolder(folderName, favorite.parents, signal);

    return FavoriteApi.add({ ...favorite, parents: `${favorite.parents ?? ''}#${folderId}#` }, signal);
  }

  static async remove(id: number, signal?: AbortSignal) {
    await Api.post(FavoriteEndPoint.Delete, { fav_id: id }, signal);
  }
}
